/**
 * Local storage keys
 */
export const STORAGE_KEYS = {
  /** Legacy, from when tabs shared one session: removed on load, never written or read (T-155). */
  AUTH_TOKEN: 'shopify-xero-sync-token',
  /** Legacy, like AUTH_TOKEN. */
  USER: 'shopify-xero-sync-user',
  THEME: 'shopify-xero-sync-theme',
  /** A team id only: the team a brand-new tab opens on. Each tab keeps its own session (T-155). */
  LAST_TEAM: 'jflo-last-team-id',
};

/** sessionStorage — Better Auth OAuth + app session exchange UX */
export const SESSION_AUTH_KEYS = {
  IN_PROGRESS: 'auth_in_progress',
  /** User left our origin mid–sign-in (e.g. redirect to Google). Survives the round-trip. */
  OAUTH_DEPARTED_ORIGIN: 'oauth_departed_origin',
  /** Where to send the user after a successful login (survives OAuth round-trip). */
  REDIRECT: 'auth_redirect',
} as const;

export function clearSessionAuthMarkers() {
  // Typed locally so this compiles in the worker too, which has no DOM types.
  const storage = (globalThis as { sessionStorage?: { removeItem(key: string): void } }).sessionStorage;
  if (typeof storage === 'undefined') return;
  storage.removeItem(SESSION_AUTH_KEYS.IN_PROGRESS);
  storage.removeItem(SESSION_AUTH_KEYS.OAUTH_DEPARTED_ORIGIN);
}

/**
 * Pagination defaults
 */
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PAGE_SIZE: 10,
  MAX_PAGE_SIZE: 100,
};

/**
 * Date formats
 */
export const DATE_FORMATS = {
  DISPLAY: 'MMMM d, yyyy',
  ISO: 'yyyy-MM-dd',
  DATETIME: 'yyyy-MM-dd HH:mm:ss',
};

/**
 * One-click marketing backfills on Integrations (Google Ads workflow, GA4, GSC, Meta, Microsoft).
 * Default ~16 months matches Search Console’s rolling retention; server clamps to MAX.
 * Individual APIs may still return less (e.g. GA4 property retention, Ahrefs plan limits).
 */
export const MARKETING_BACKFILL_DEFAULT_DAYS = 490;
/** Hard ceiling for marketing backfill `days` params (≈2 years). */
export const MARKETING_BACKFILL_MAX_DAYS = 730;

/**
 * Ahrefs free Domain Rating: email team admins only after this many consecutive UTC days
 * without a successful API sync (avoids one-night 429 spam).
 */
export const AHREFS_EMAIL_CONSECUTIVE_FAILURE_DAYS = 3;
/**
 * Seconds between each team's Ahrefs call in the daily marketing cron (shared Worker egress).
 * Team index 0 waits 0s, index 1 waits this value, etc.
 */
export const AHREFS_TEAM_STAGGER_SECONDS = 90;

/**
 * Instagram user insights retention is up to ~90 days (Meta docs).
 * Cap backfills to that window so we don't invent older history.
 */
export const INSTAGRAM_BACKFILL_DEFAULT_DAYS = 90;
export const INSTAGRAM_BACKFILL_MAX_DAYS = 90;

/**
 * Rolling window for daily Instagram sync / Sync now (inclusive UTC days).
 * Includes today so live follower totals can land; re-fetches recent days for late insights.
 */
export const INSTAGRAM_DAILY_SYNC_DAYS = 7;

/**
 * Rolling window for Google / Microsoft / Meta Ads daily sync and Sync now.
 * Ends yesterday (UTC); re-fetches so delayed conversion attribution can catch up.
 * Spend/clicks change little after the fact; Orders/Revenue often mature over weeks.
 */
export const ADS_DAILY_ATTRIBUTION_LOOKBACK_DAYS = 60;

/**
 * Rolling window for GA4 daily sync / Sync now (inclusive UTC days ending yesterday).
 * Closes short outages after reconnect without requiring a full backfill.
 */
export const GA4_DAILY_SYNC_DAYS = 30;

/**
 * Search Console daily sync / Sync now: settled days ending at today−lag (UTC).
 * GSC data is incomplete for recent days; keep end date behind the lag.
 */
export const SEARCH_CONSOLE_DAILY_SYNC_DAYS = 14;
export const SEARCH_CONSOLE_DATA_LAG_DAYS = 4;

/**
 * Shopify marketing backfill window (Shopify Email newsletters via ShopifyQL).
 * Matches the 24-month ShopifyQL `SINCE` used by the backfill workflow.
 */
export const SHOPIFY_MARKETING_BACKFILL_DEFAULT_DAYS = 730;
/** Rolling window refreshed on daily sync / Sync now. */
export const SHOPIFY_MARKETING_DAILY_REFRESH_DAYS = 90;
/** Backfill workflow step size (~6 months) to stay within Worker time limits. */
export const SHOPIFY_MARKETING_BACKFILL_WINDOW_DAYS = 180;
/** Pause between backfill windows so ShopifyQL cost budget can recover. */
export const SHOPIFY_MARKETING_BACKFILL_WINDOW_PAUSE = '15 seconds';

/**
 * If a marketing backfill stays in an active state without a status update for this long,
 * treat it as stale so the UI stops polling and the user can retry.
 * Sized for ShopifyQL rate-limit backoff + inter-window pauses.
 */
export const MARKETING_BACKFILL_STALE_MS = 20 * 60 * 1000;

/**
 * Cap on feed/reel media items per Instagram sync (Graph allows up to 10k;
 * each item needs an insights call, so keep this bounded for Worker time).
 */
export const INSTAGRAM_MEDIA_SYNC_MAX = 250;
/** Page size when listing `/{ig-user-id}/media`. */
export const INSTAGRAM_MEDIA_PAGE_SIZE = 100;

/**
 * Facebook Page organic (Graph API v26.0).
 *
 * Verified contract (Brave Hardy Page probe, 2026-09-01):
 * - Page Insights: day period; max 90 days per since/until; ~2 years retention.
 * - Valid page metrics: page_follows, page_daily_follows_unique,
 *   page_daily_unfollows_unique, page_fan_adds_by_paid_non_paid_unique,
 *   page_media_view (+ is_from_ads / is_from_followers), page_total_media_view_unique,
 *   page_post_engagements, page_views_total, page_total_actions.
 * - Invalid on v26 (do not request): page_fan_adds, page_fan_removes,
 *   page_negative_feedback*, legacy page_posts_impressions* / reach uniques.
 * - Posts: GET /{page-id}/published_posts (limit ≤100; ~600 ranked posts/year);
 *   requires pages_read_user_content. Lifetime post insights: post_media_view,
 *   post_total_media_view_unique, reactions/clicks, video retention metrics.
 * - Scopes: Facebook organic needs read_insights + pages_read_user_content
 *   (plus pages_show_list + pages_read_engagement). Do not add those to Meta Ads /
 *   Instagram OAuth unless reconnecting that product.
 * - v26 rejects root GET /?ids=… and date_format; use per-object or Graph batch.
 */
export const FACEBOOK_DAILY_SYNC_DAYS = 7;
/** Recent posts refreshed on daily sync (lifetime snapshot refresh window). */
export const FACEBOOK_POST_REFRESH_DAYS = 90;
/**
 * Default Integrations backfill uses platform MARKETING_BACKFILL_DEFAULT_DAYS (490).
 * Page Insights retain up to ~2 years; clamp to MARKETING_BACKFILL_MAX_DAYS (730).
 */
export const FACEBOOK_BACKFILL_DEFAULT_DAYS = 490;
export const FACEBOOK_BACKFILL_MAX_DAYS = 730;
/** Max Page Insights days per Graph since/until request. */
export const FACEBOOK_INSIGHTS_WINDOW_DAYS = 90;
/** Cap published posts fetched/insights per sync (Worker time + Graph rate limits). */
export const FACEBOOK_POST_SYNC_MAX = 250;
/** Page size when listing /{page-id}/published_posts (Graph max 100). */
export const FACEBOOK_POST_PAGE_SIZE = 100;

/**
 * Error messages
 */
export const ERROR_MESSAGES = {
  UNAUTHORIZED: 'You are not authorized to perform this action',
  NOT_FOUND: 'The requested resource was not found',
  SERVER_ERROR: 'An unexpected error occurred. Please try again later',
  VALIDATION_ERROR: 'Please check your input and try again',
  CONNECTION_ERROR: 'Could not connect to the server. Please check your internet connection',
};

/**
 * Success messages
 */
export const SUCCESS_MESSAGES = {
  LOGIN: 'You have successfully logged in',
  LOGOUT: 'You have successfully logged out',
  PROFILE_UPDATE: 'Your profile has been updated successfully',
  SHOPIFY_CONNECT: 'Your Shopify store has been connected successfully',
  XERO_CONNECT: 'Your Xero account has been connected successfully',
  SYNC_STARTED: 'Sync job has been started successfully',
};

/**
 * The most a receiving or wholesale line may be counted at. A barcode scanned into a quantity box
 * reads as an 8- to 14-digit number (an EAN-13 is in the trillions), so a count far above what the
 * line expects is refused (T-156, B7): ten times the expected quantity, and never under a million,
 * so large orders and lines combined from several purchase orders still fit.
 */
export function maxCountFor(expectedQty: number): number {
  return Math.max(1_000_000, Math.ceil((Number.isFinite(expectedQty) ? expectedQty : 0) * 10));
}
export const COUNT_TOO_BIG_MESSAGE =
  'That count is too big. Was a barcode scanned into the quantity box?';
